import { IAccount, ISubscription } from "@/types";
import axios, { axiosPrivate } from "./axios";
import { tasks_v1 } from "@googleapis/tasks";
import { calendar_v3 } from "@googleapis/calendar";
import { ExtendedTask, ExtendedTaskList } from "@/components/Task/types";

export const login = async (code: string) => {
  const res = await axios.post(
    "/login",
    { code },
    { withCredentials: true }
  );
  return res.data;
}

export const getAccounts = async () => {
  const response = await axiosPrivate.get("/accounts");
  return response.data as Record<string, IAccount>;
}

export const logout = async () => {
  await axiosPrivate.get("/logout")
}

export const getTasksListsForUser = async (account: IAccount): Promise<ExtendedTaskList[]> => {
  const res = await axiosPrivate.get(`/tasklists/${account?.id}`)
  const data: tasks_v1.Schema$TaskList[] = res.data;
  return data.map(d => ({ ...d, accountId: account.id }));
}

export const getTasksListForUser = async (account: IAccount, tasklistId: string): Promise<ExtendedTask[]> => {
  const res = await axiosPrivate.get(`/tasks/${tasklistId}/${account?.id}`)
  const data: tasks_v1.Schema$Task[] = res.data || [];
  return data.map(d => ({ ...d, accountId: account.id, tasklistId }))
    .sort((a, b) => a?.position?.localeCompare(b?.position || "") || 0) // TODO sort in hook so that there is a function this returns exactly what the API returns
}

export const getCompletedTasksForUser = async (
  account: IAccount,
  tasklistId: string,
  pageToken?: string
): Promise<{ items: ExtendedTask[]; nextPageToken: string | null }> => {
  const params = pageToken ? `?pageToken=${pageToken}` : '';
  const res = await axiosPrivate.get(`/tasks/${tasklistId}/${account?.id}/completed${params}`);
  const data = res.data;
  return {
    items: (data.items || []).map((d: tasks_v1.Schema$Task) => ({ ...d, accountId: account.id, tasklistId })),
    nextPageToken: data.nextPageToken,
  };
};

export const updateTask = async (account: IAccount, tasklist: string, task: string, updatedFields: Partial<tasks_v1.Schema$Task>) => {
  const body = { uid: account.id, tasklist, task, updatedFields };
  const res = await axiosPrivate.put(`/tasks/${account.id}`, body) // TODO better error handling
  return res.data;
}

export const insertTask = async (account: IAccount, tasklist: string, task: Partial<tasks_v1.Schema$Task>) => {
  const body = { tasklist, task };
  const res = await axiosPrivate.post(`/tasks/${account.id}`, body) // TODO better error handling
  return res.data;
}

export const moveTask = async (account: IAccount, tasklist: string, taskId: string, options: {
  parent?: string;
  previous?: string;
  destinationTasklist?: string;
}) => {
  const body = { 
    tasklist, 
    task: taskId,
    ...options
  };
  const res = await axiosPrivate.post(`/tasks/move/${account.id}`, body);
  return res.data;
}

export const createTasklist = async (account: IAccount, title: string) => {
  const body = { title };
  const res = await axiosPrivate.post(`/tasklists/${account.id}`, body)
  return res.data;
}

export const deleteTasklist = async (account: IAccount, tasklistId: string) => {
  const res = await axiosPrivate.delete(`/tasklists/${tasklistId}/${account.id}`)
  return res.data;
}

export const deleteTask = async (account: IAccount, tasklistId: string, taskId: string) => {
  const body = { tasklist: tasklistId, task: taskId };
  const res = await axiosPrivate.delete(`/tasks/${account.id}`, { data: body })
  return res.data;
}

export const saveConsent = async (email: string, consent: boolean) => {
  const body = { email, consent };
  const res = await axiosPrivate.post("/store-consent", body);
  return res.data;
}

export const updateAccount = async (account: IAccount, updatedFields: Partial<IAccount>) => {
  const res = await axiosPrivate.put(`/accounts/${account.id}`, updatedFields);
  return res.data;
}

export const disconnectAccount = async (account: IAccount) => {
  const res = await axiosPrivate.delete(`/accounts/${account.id}`);
  return res.data;
}

export const shareList = async (account: IAccount, listId: string) => {
  const res = await axiosPrivate.post(`/share-list`, { listId, accountId: account.id });
  return res.data;
}

export const getUserConfig = async () => {
  const res = await axiosPrivate.get('/api/config');
  return res.data;
}

export const updateUserConfig = async (config: any) => {
  const res = await axiosPrivate.put('/api/config', config);
  return res.data;
}

export const cleanupAccountConfig = async (accountId: string) => {
  const res = await axiosPrivate.delete(`/api/config/accounts/${accountId}`);
  return res.data;
}

export const checkTaskFusionCalendar = async (_accountId: string, userId: string) => {
  const res = await axiosPrivate.get(`/calendars/task-fusion/${userId}`);
  return res.data;
}

export const createTaskFusionCalendar = async (_accountId: string, userId: string) => {
  const res = await axiosPrivate.post(`/calendars/task-fusion/${userId}`);
  return res.data;
}

export const getTaskFusionCalendar = async (_accountId: string, userId: string) => {
  const res = await axiosPrivate.get(`/calendars/task-fusion/${userId}`);
  return res.data;
}

export const getCalendarEvents = async (_accountId: string, calendarId: string, userId: string, timeMin?: string, timeMax?: string) => {
  const params = new URLSearchParams();
  if (timeMin) params.append('timeMin', timeMin);
  if (timeMax) params.append('timeMax', timeMax);
  const queryString = params.toString();
  const url = `/calendars/events/${calendarId}/${userId}${queryString ? `?${queryString}` : ''}`;
  const res = await axiosPrivate.get(url);
  return res.data as calendar_v3.Schema$Event[];
}

export const createEventFromTask = async (_accountId: string, calendarId: string, task: any, userId: string) => {
  // Use task.startTime if provided (for drag-and-drop with specific time), otherwise default to 9am
  let startTime: string;
  if (task.startTime) {
    startTime = task.startTime;
  } else if (task.due) {
    startTime = new Date(task.due).toISOString().split('T')[0] + 'T09:00:00Z';
  } else {
    startTime = new Date().toISOString().split('T')[0] + 'T09:00:00Z';
  }
  
  const res = await axiosPrivate.post(`/calendars/events/${calendarId}/${userId}`, {
    task,
    accountId: _accountId,
    startTime
  });
  return res.data as calendar_v3.Schema$Event;
}

export const getCalendarEvent = async (_accountId: string, calendarId: string, eventId: string, userId: string) => {
  try {
    const res = await axiosPrivate.get(`/calendars/events/${calendarId}/${eventId}/${userId}`);
    return res.data as calendar_v3.Schema$Event;
  } catch (error: any) {
    // Handle 404 as valid response (event doesn't exist)
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

export const deleteCalendarEvent = async (_accountId: string, calendarId: string, eventId: string, userId: string) => {
  const res = await axiosPrivate.delete(`/calendars/events/${calendarId}/${eventId}/${userId}`);
  return res.data;
}

export const updateCalendarEvent = async (_accountId: string, calendarId: string, eventId: string, task: any, userId: string) => {
  const res = await axiosPrivate.patch(`/calendars/events/${calendarId}/${eventId}/${userId}`, { task });
  return res.data as calendar_v3.Schema$Event;
}

export const updateCalendarEventTime = async (
  _accountId: string,
  calendarId: string,
  eventId: string,
  startTime: string,
  endTime: string,
  userId: string
) => {
  const res = await axiosPrivate.patch(`/calendars/events/${calendarId}/${eventId}/${userId}`, {
    task: { startTime, endTime }
  });
  return res.data as calendar_v3.Schema$Event;
}

export const listCalendars = async (_accountId: string, userId: string) => {
  const res = await axiosPrivate.get(`/calendars/${userId}`);
  return res.data as calendar_v3.Schema$CalendarListEntry[];
}

export const getTaskEventMappings = async (accountId: string, userId: string) => {
  const res = await axiosPrivate.get(`/calendars/task-event-mappings/${accountId}/${userId}`);
  return res.data as Record<string, { taskId: string; accountId: string; calendarId: string; eventId: string; createdAt: any }>;
}

export const deleteTaskEventMapping = async (accountId: string, taskId: string, userId: string) => {
  const res = await axiosPrivate.delete(`/calendars/task-event-mappings/${accountId}/${taskId}/${userId}`);
  return res.data;
}

// Subscription
export const getSubscriptionStatus = async () => {
  const res = await axiosPrivate.get('/api/subscription/status');
  return res.data as ISubscription;
}

export const createCheckout = async (variantId: string, redirectUrl?: string) => {
  const res = await axiosPrivate.post('/api/subscription/checkout', { variantId, redirectUrl });
  return res.data as { url: string };
}

export const getCustomerPortalUrl = async () => {
  const res = await axiosPrivate.get('/api/subscription/portal');
  return res.data as { url: string };
}